import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createUserContextSlice } from './userContextSlice';
import { createActionsSlice } from './actionsSlice';
import { createUISlice } from './uiSlice';
import { createMessagesSlice } from './messagesSlice';
import { createStylingSlice } from './stylingSlice';
import { createConfigSlice } from './configSlice';
import { createTriggerSlice } from './triggerSlice';
import { createBackendConnectionSlice } from './backendConnectionSlice';
import { createChatInputSlice } from './chatInputSlice';
import { createSnapshotSlice } from './snapshotSlice';
import { createOnboardingSlice } from './onboardingSlice';
import { createUserStateSlice } from './userStateSlice';
import { CedarStore } from './types';

// Re-export all necessary types
export type { CedarStore, StylingConfig } from './types';
export type {
	UserContextSlice,
	UserInfo,
	UserMetadata,
} from './userContextSlice';
export type { ActionsSlice, Action } from './actionsSlice';
export type { UISlice } from './uiSlice';
export type { MessagesSlice, Message } from './messagesSlice';
export type { StylingSlice } from './stylingSlice';
export type { ConfigSlice } from './configSlice';
export type { TriggerSlice, LoadingState } from './triggerSlice';
export type { ChatInputSlice } from './chatInputSlice';
export type { SnapshotSlice, DOMSnapshot, ActionFlow } from './snapshotSlice';
export type {
	OnboardingSlice,
	OnboardingStep,
	ActivateOnboardingSteps,
} from './onboardingSlice';
export type {
	UserStateSlice,
	UserState,
	BasicStateValue,
	EnumOptions,
	Setter,
	SetterFunction,
	SetterParameter,
} from './userStateSlice';
// Export trigger types from triggerTypes.ts
export type {
	Trigger,
	TriggerLog,
	TriggerAction,
	TriggerCondition,
	Condition,
} from '@/components/triggers/triggerTypes';

// Create the combined store
export const useCedarStore = create<CedarStore>()(
	persist(
		(...a) => ({
			...createUserContextSlice(...a),
			...createActionsSlice(...a),
			...createUISlice(...a),
			...createMessagesSlice(...a),
			...createStylingSlice(...a),
			...createConfigSlice(...a),
			...createTriggerSlice(...a),
			...createBackendConnectionSlice(...a),
			...createChatInputSlice(...a),
			...createSnapshotSlice(...a),
			...createOnboardingSlice(...a),
			...createUserStateSlice(...a),
		}),
		{
			name: 'cedar-store',
			partialize: (state) => ({
				showButton: state.showButton,
				showCedarChat: state.showCedarChat,
				isCedarChatEnabled: state.isCedarChatEnabled,
				styling: state.styling,
				productId: state.productId,
				flags: state.flags,
			}),
		}
	)
);

// Export the set function directly
export const setCedarStore = useCedarStore.setState;

// Export selectors for convenience
export const useUserContext = () => ({
	userInfo: useCedarStore((state) => state.userInfo),
	userMetadata: useCedarStore((state) => state.userMetadata),
	userId: useCedarStore((state) => state.userId),
	setUserInfo: useCedarStore((state) => state.setUserInfo),
	updateUserInfo: useCedarStore((state) => state.updateUserInfo),
	setUserMetadata: useCedarStore((state) => state.setUserMetadata),
	updateUserMetadata: useCedarStore((state) => state.updateUserMetadata),
	setUserId: useCedarStore((state) => state.setUserId),
});

export const useActions = () => ({
	queue: useCedarStore((state) => state.queue),
	currentAction: useCedarStore((state) => state.currentAction),
	addAction: useCedarStore((state) => state.addAction),
	addActions: useCedarStore((state) => state.addActions),
	removeAction: useCedarStore((state) => state.removeAction),
	clearQueue: useCedarStore((state) => state.clearQueue),
	replaceActions: useCedarStore((state) => state.replaceActions),
	nextAction: useCedarStore((state) => state.nextAction),
	isActive: useCedarStore((state) => state.isActive),
	setIsActive: useCedarStore((state) => state.setIsActive),
	prevCursorPosition: useCedarStore((state) => state.prevCursorPosition),
	setPrevCursorPosition: useCedarStore((state) => state.setPrevCursorPosition),
	isAnimatingOut: useCedarStore((state) => state.isAnimatingOut),
	setIsAnimatingOut: useCedarStore((state) => state.setIsAnimatingOut),
	onQueueComplete: useCedarStore((state) => state.onQueueComplete),
	addActionsToStart: useCedarStore((state) => state.addActionsToStart),
	setActionSessionId: useCedarStore((state) => state.setActionSessionId),
	setCurrentAction: useCedarStore((state) => state.setCurrentAction),
});

export const useUI = () => ({
	showButton: useCedarStore((state) => state.showButton),
	showCedarChat: useCedarStore((state) => state.showCedarChat),
	isCedarChatEnabled: useCedarStore((state) => state.isCedarChatEnabled),
	modalComponent: useCedarStore((state) => state.modalComponent),
	cedarChatConfig: useCedarStore((state) => state.cedarChatConfig),
	variables: useCedarStore((state) => state.variables),
	flags: useCedarStore((state) => state.flags),
	setShowButton: useCedarStore((state) => state.setShowButton),
	setShowCedarChat: useCedarStore((state) => state.setShowCedarChat),
	setIsCedarChatEnabled: useCedarStore((state) => state.setIsCedarChatEnabled),
	setModalComponent: useCedarStore((state) => state.setModalComponent),
	setCedarChatConfig: useCedarStore((state) => state.setCedarChatConfig),
	setVariable: useCedarStore((state) => state.setVariable),
	setFlag: useCedarStore((state) => state.setFlag),
	setVariables: useCedarStore((state) => state.setVariables),
	setFlags: useCedarStore((state) => state.setFlags),
	removeVariable: useCedarStore((state) => state.removeVariable),
	removeFlag: useCedarStore((state) => state.removeFlag),
	clearVariables: useCedarStore((state) => state.clearVariables),
	clearFlags: useCedarStore((state) => state.clearFlags),
	toggleButton: useCedarStore((state) => state.toggleButton),
	toggleCedarChat: useCedarStore((state) => state.toggleCedarChat),
	toggleIsCedarChatEnabled: useCedarStore(
		(state) => state.toggleIsCedarChatEnabled
	),
});

export const useBackendConnection = () => ({
	sendMessage: useCedarStore((state) => state.sendMessage),
	sendMessageStream: useCedarStore((state) => state.sendMessageStream),
	getChatHistory: useCedarStore((state) => state.getChatHistory),
	deleteChatThread: useCedarStore((state) => state.deleteChatThread),
	agentType: useCedarStore((state) => state.agentType),
	setAgentType: useCedarStore((state) => state.setAgentType),
});

export const useMessages = () => ({
	messages: useCedarStore((state) => state.messages),
	isProcessing: useCedarStore((state) => state.isProcessing),
	unreadMessages: useCedarStore((state) => state.unreadMessages),
	suggestions: useCedarStore((state) => state.suggestions),
	isBarActive: useCedarStore((state) => state.isBarActive),
	setMessages: useCedarStore((state) => state.setMessages),
	addMessage: useCedarStore((state) => state.addMessage),
	clearMessages: useCedarStore((state) => state.clearMessages),
	setIsProcessing: useCedarStore((state) => state.setIsProcessing),
	setUnreadMessages: useCedarStore((state) => state.setUnreadMessages),
	incrementUnreadMessages: useCedarStore(
		(state) => state.incrementUnreadMessages
	),
	resetUnreadMessages: useCedarStore((state) => state.resetUnreadMessages),
	nextMessage: useCedarStore((state) => state.nextMessage),
	initializeChat: useCedarStore((state) => state.initializeChat),
	deleteChatThread: useCedarStore((state) => state.deleteChatThread),
	setSuggestions: useCedarStore((state) => state.setSuggestions),
	clickSuggestion: useCedarStore((state) => state.clickSuggestion),
	setIsBarActive: useCedarStore((state) => state.setIsBarActive),
});

// Export a hook for chat input
export const useChatInput = () => ({
	// chatInputContent: useCedarStore((state) => state.chatInputContent),
	overrideInputContent: useCedarStore((state) => state.overrideInputContent),
	setChatInputContent: useCedarStore((state) => state.setChatInputContent),
	setOverrideInputContent: useCedarStore(
		(state) => state.setOverrideInputContent
	),
});

// Export a hook for styling config
export const useStyling = () => ({
	styling: useCedarStore((state) => state.styling),
	setStyling: useCedarStore((state) => state.setStyling),
});

// Export a hook for product config
export const useConfig = () => ({
	productId: useCedarStore((state) => state.productId),
	setProductId: useCedarStore((state) => state.setProductId),
	userId: useCedarStore((state) => state.userId),
	setUserId: useCedarStore((state) => state.setUserId),
	validateOrCreateProductUser: useCedarStore(
		(state) => state.validateOrCreateProductUser
	),
	updateProductUserId: useCedarStore((state) => state.updateProductUserId),
	addEmailToProductUser: useCedarStore((state) => state.addEmailToProductUser),
	supabaseConfig: useCedarStore((state) => state.supabaseConfig),
	setSupabaseConfig: useCedarStore((state) => state.setSupabaseConfig),
});

// Export a hook for triggers
export const useTriggers = () => ({
	triggers: useCedarStore((state) => state.triggers),
	triggerLogs: useCedarStore((state) => state.triggerLogs),
	triggerLoadingState: useCedarStore((state) => state.triggerLoadingState),
	setTriggers: useCedarStore((state) => state.setTriggers),
	setTriggerLogs: useCedarStore((state) => state.setTriggerLogs),
	setTriggerLoadingState: useCedarStore(
		(state) => state.setTriggerLoadingState
	),
	callTrigger: useCedarStore((state) => state.callTrigger),
	markTriggerLogComplete: useCedarStore(
		(state) => state.markTriggerLogComplete
	),
	createTriggerLog: useCedarStore((state) => state.createTriggerLog),
});

export const useSuggestions = () => ({
	suggestions: useCedarStore((state) => state.suggestions),
	clickSuggestion: useCedarStore((state) => state.clickSuggestion),
});

// Export a hook for snapshot functionality
export const useSnapshots = () => ({
	isRecording: useCedarStore((state) => state.isRecording),
	currentActionFlow: useCedarStore((state) => state.currentActionFlow),
	savedActionFlows: useCedarStore((state) => state.savedActionFlows),
	startRecording: useCedarStore((state) => state.startRecording),
	stopRecording: useCedarStore((state) => state.stopRecording),
	captureSnapshot: useCedarStore((state) => state.captureSnapshot),
	clearCurrentActionFlow: useCedarStore(
		(state) => state.clearCurrentActionFlow
	),
	deleteActionFlow: useCedarStore((state) => state.deleteActionFlow),
	renameActionFlow: useCedarStore((state) => state.renameActionFlow),
	updateActionFlowDescription: useCedarStore(
		(state) => state.updateActionFlowDescription
	),
	exportActionFlow: useCedarStore((state) => state.exportActionFlow),
	importActionFlow: useCedarStore((state) => state.importActionFlow),
});

// Add a new hook for onboarding
export const useOnboarding = () => ({
	onboardingSteps: useCedarStore((state) => state.onboardingSteps),
	stepNum: useCedarStore((state) => state.stepNum),
	isOnboardingActive: useCedarStore((state) => state.isOnboardingActive),
	isOnboardingComplete: useCedarStore((state) => state.isOnboardingComplete),
	progressBar: useCedarStore((state) => state.progressBar),
	variables: useCedarStore((state) => state.variables),
	setOnboardingSteps: useCedarStore((state) => state.setOnboardingSteps),
	addOnboardingStep: useCedarStore((state) => state.addOnboardingStep),
	addOnboardingSteps: useCedarStore((state) => state.addOnboardingSteps),
	removeOnboardingStep: useCedarStore((state) => state.removeOnboardingStep),
	clearOnboardingSteps: useCedarStore((state) => state.clearOnboardingSteps),
	nextStep: useCedarStore((state) => state.nextStep),
	prevStep: useCedarStore((state) => state.prevStep),
	goToStep: useCedarStore((state) => state.goToStep),
	setOnboardingStepValue: useCedarStore(
		(state) => state.setOnboardingStepValue
	),
	setIsOnboardingActive: useCedarStore((state) => state.setIsOnboardingActive),
	completeOnboarding: useCedarStore((state) => state.completeOnboarding),
	resetOnboarding: useCedarStore((state) => state.resetOnboarding),
	setProgressBarValue: useCedarStore((state) => state.setProgressBarValue),
	setProgressBarTotal: useCedarStore((state) => state.setProgressBarTotal),
	applyStepBonus: useCedarStore((state) => state.applyStepBonus),
	getStepValue: useCedarStore((state) => state.getStepValue),
	renderStepText: useCedarStore((state) => state.renderStepText),
	setVariable: useCedarStore((state) => state.setVariable),
	setVariables: useCedarStore((state) => state.setVariables),
	removeVariable: useCedarStore((state) => state.removeVariable),
	clearVariables: useCedarStore((state) => state.clearVariables),
	upsertOnboardingSteps: useCedarStore((state) => state.upsertOnboardingSteps),
	setProgressBar: useCedarStore((state) => state.setProgressBar),
	activateOnboardingSteps: useCedarStore(
		(state) => state.activateOnboardingSteps
	),
});

// Add a hook for user state
export const useUserState = () => ({
	userStates: useCedarStore((state) => state.userStates),
	registerState: useCedarStore((state) => state.registerState),
	getState: useCedarStore((state) => state.getState),
	addCustomSetters: useCedarStore((state) => state.addCustomSetters),
});

// Export a hook for flags
export const useFlags = () => ({
	flags: useCedarStore((state) => state.flags),
	setFlag: useCedarStore((state) => state.setFlag),
	setFlags: useCedarStore((state) => state.setFlags),
	removeFlag: useCedarStore((state) => state.removeFlag),
	clearFlags: useCedarStore((state) => state.clearFlags),
});
